/**
 * The surfaces that render presigned bearer URLs (#2326 rounds 15 + 22).
 *
 * PostHog masks the signature at its dispatch/serialization chokepoints
 * (`lib/analytics/presigned-url-mask.ts`) and keeps recording. Every OTHER
 * analytics vendor is suppressed outright on these routes:
 *
 *  - **Microsoft Clarity** — its recorder has no attribute-masking hook;
 *  - **Google Analytics 4** — enhanced-measurement OUTBOUND CLICKS ship the
 *    full `link_url` (signature included) to Google when a consented user
 *    clicks a presigned link, and enhanced measurement has NO client-side
 *    off switch (it is data-stream admin config), so suppression is the only
 *    code-side control;
 *  - **Microsoft UET** — its automatic-event collection is likewise not
 *    DOM-attribute-safe.
 *
 * The browser user's authorisation does not extend to analytics vendors; the
 * round-13 ruling applies identically to each of them.
 *
 * The list is pinned against drift by `__tests__/analytics/signed-url-surfaces.test.ts`:
 * every directory under `app/(app)` (and the signed-URL-bearing top-level
 * product routes) must appear here, so a new authenticated surface cannot
 * silently re-enable a vendor.
 */
export const SIGNED_URL_SURFACE_PREFIXES: readonly string[] = [
  // app/(app) — the authenticated product
  "/admin",
  "/approvals",
  "/billing",
  "/company-documents",
  "/compliance",
  "/compliance-primer",
  "/coshh",
  "/dashboard",
  "/documents",
  "/plant",
  "/portal",
  "/projects",
  "/referrals",
  "/saved-data",
  "/settings",
  "/subcontractors",
  "/team",
  "/workers",
  // top-level product surfaces that render object URLs or portal evidence
  "/crew",
  "/embed",
  "/equipment",
  "/procore",
  "/share",
  "/shared",
  "/sign",
  "/site",
];

/**
 * Documented exemptions — routes under a suppressed prefix that render NO
 * presigned object URLs, for the GOOGLE tag only.
 *
 *  - `/billing/success` — the post-checkout conversion surface. The Google
 *    Ads purchase conversion fires ONLY here (client-side gtag; there is no
 *    server-side Ads fallback — GA4's purchase survives via the Stripe
 *    webhook's Measurement-Protocol mirror, but the Ads tag does not).
 *    Sweeping `/billing` in with the presigned-URL surfaces silenced 100%
 *    of Google Ads ROAS signal. The page renders no presigned URLs; the
 *    `?session_id=` it carries is Stripe's own checkout reference, not an
 *    object bearer.
 *
 * Microsoft UET deliberately STAYS suppressed on all of /billing — that is
 * the pinned #2326 privacy ruling
 * (e2e/local/billing-success-microsoft-conversion.spec.ts proves the product
 * route does not bypass it) — and Clarity keeps the base list for the same
 * attribute-masking reason.
 */
const GOOGLE_TAG_EXEMPT_PREFIXES: readonly string[] = [
  "/billing/success",
];

/** True when analytics vendors without attribute masking must not run here. */
export function isSignedUrlSurfacePath(pathname: string): boolean {
  const normalised = pathname.startsWith("/") ? pathname : `/${pathname}`;
  return SIGNED_URL_SURFACE_PREFIXES.some(
    (prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`),
  );
}

/**
 * The GOOGLE tag's suppression test: the base presigned-URL surface list
 * minus the documented conversion-surface exemptions above. gtag has no
 * attribute-masking gap on a page that renders no presigned URLs, and the
 * Google Ads purchase conversion has no server-side fallback.
 */
export function isGoogleTagSuppressedPath(pathname: string): boolean {
  const normalised = pathname.startsWith("/") ? pathname : `/${pathname}`;
  if (
    GOOGLE_TAG_EXEMPT_PREFIXES.some(
      (prefix) => normalised === prefix || normalised.startsWith(`${prefix}/`),
    )
  ) {
    return false;
  }
  return isSignedUrlSurfacePath(normalised);
}
